import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { resellerFetch } from "../../api/client";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/**
 * Wave 1.3 — Reseller profile page (v1: read-only account details).
 *
 * Sub-user self-management is intentionally NOT here yet: the backend
 * endpoints (GET/POST /resellers/{id}/users) are admin-guarded. A
 * /resellers/me/users endpoint is a Wave 5.5 follow-up.
 */
export default function ResellerProfilePage() {
  const [profile, setProfile] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await resellerFetch(`${API_BASE_URL}/api/v1/resellers/me`);
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.detail || "Failed to load profile");
        }
        const data = await response.json();
        if (!cancelled) setProfile(data);
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || "Profile not found"}</Alert>
      </Box>
    );
  }

  const rows: Array<[string, any]> = [
    ["Account", profile.account],
    ["Full hierarchy path", profile.full_account_path],
    ["Tenant", profile.tenant],
    ["Level", profile.level ?? "—"],
    ["Balance", `$${profile.balance?.toFixed(2) ?? "0.00"}`],
    [
      "Credit limit",
      profile.credit_limit != null ? `$${profile.credit_limit.toFixed(2)}` : "None",
    ],
    [
      "Margin",
      profile.margin_percent != null ? `${profile.margin_percent}%` : "None",
    ],
    [
      "Margin floor",
      profile.margin_floor != null ? `$${profile.margin_floor.toFixed(4)}` : "None",
    ],
    ["Max downstream depth", profile.max_depth ?? "—"],
    ["Created", new Date(profile.created_at).toLocaleString()],
    ["Last updated", new Date(profile.updated_at).toLocaleString()],
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Profile
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Your reseller account details as configured by your upstream provider.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography variant="h6">Account Details</Typography>
                <Chip
                  label={profile.disabled ? "Disabled" : "Active"}
                  color={profile.disabled ? "default" : "success"}
                  size="small"
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
              {rows.map(([label, value]) => (
                <Box
                  key={label}
                  display="flex"
                  justifyContent="space-between"
                  py={0.75}
                  flexWrap="wrap"
                  gap={1}
                >
                  <Typography variant="body2" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: typeof value === "string" && value.includes(":") ? "monospace" : "inherit",
                      fontWeight: label === "Balance" ? 600 : 400,
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
